/**
 * Engineering scales: spacing, type, elevation and motion.
 *
 * THESE ARE ENGINEERING ARTIFACTS, NOT A VISUAL DESIGN.
 *   FE-1 Stage Work Item §4.1 D authorizes "theme mechanism, token layer (color,
 *   spacing, type scale, elevation, motion), light/dark mechanism" and states in
 *   the same breath: "Tokens are engineering artifacts; no visual design, brand or
 *   aesthetic is approved." Nothing below chooses a look. The scales are a
 *   vocabulary of names so that later, separately authorized design work has
 *   somewhere to land instead of inventing literals inside components.
 *
 * NO OPERATIONAL VALUE APPEARS HERE.
 *   No threshold, TTL, timeout, distance, altitude or safety value. `DURATION`
 *   values are animation timings for UI transitions and are not a protocol or
 *   device timing.
 */

/** A closed set of spacing steps. `NONE` exists so "no spacing" is sayable. */
export const SPACING = Object.freeze({
  NONE: '0',
  XXS: '0.25rem',
  XS: '0.5rem',
  SM: '0.75rem',
  MD: '1rem',
  LG: '1.5rem',
  XL: '2rem',
  XXL: '3rem',
});

/** Type scale steps. Sizes only — no family decision beyond a system stack. */
export const TYPE_SCALE = Object.freeze({
  XS: '0.75rem',
  SM: '0.875rem',
  MD: '1rem',
  LG: '1.25rem',
  XL: '1.5rem',
  XXL: '2rem',
});

/**
 * System font stacks only. No webfont is fetched: a webfont would be an external
 * network dependency, and FE1-W0 ships none.
 */
export const FONT_FAMILY = Object.freeze({
  BASE: 'system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  MONO: 'ui-monospace, SFMono-Regular, Menlo, Consolas, "Liberation Mono", monospace',
});

export const FONT_WEIGHT = Object.freeze({
  REGULAR: '400',
  MEDIUM: '500',
  SEMIBOLD: '600',
});

export const LINE_HEIGHT = Object.freeze({
  TIGHT: '1.25',
  NORMAL: '1.5',
  RELAXED: '1.75',
});

export const ELEVATION = Object.freeze({
  FLAT: 'none',
  LOW: '0 1px 2px rgb(0 0 0 / 12%)',
  MID: '0 2px 8px rgb(0 0 0 / 16%)',
  HIGH: '0 8px 24px rgb(0 0 0 / 24%)',
});

/**
 * Motion durations and easings for UI transitions.
 *
 * NOT device, protocol or command timings. `INSTANT` exists so that reduced-motion
 * handling has a named target rather than an inline zero.
 */
export const DURATION = Object.freeze({
  INSTANT: '0ms',
  FAST: '120ms',
  NORMAL: '200ms',
  SLOW: '320ms',
});

export const EASING = Object.freeze({
  STANDARD: 'cubic-bezier(0.2, 0, 0, 1)',
  EMPHASIS: 'cubic-bezier(0.2, 0, 0, 1.2)',
});
